

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaPlus, FaTrash, FaEdit, FaUsers, FaCheck, FaUpload, FaEye,
  FaChevronLeft, FaChevronRight, FaSearch, FaUserTimes, FaDownload
} from "react-icons/fa";
import { toast } from "react-toastify";

import { 
  getLeads, 
  deleteLead, 
  getLeadStatuses, 
  deleteMultipleLeads, 
  unassignLeads // New unassign API
} from "../../services/leadService";
import { getCategories } from "../../services/categoryService";

import AssignLeadModal from "./AssignLeadModal";
import UploadLeadModal from "./UploadLeadModal";
import "./LeadListLayer.css";

const LeadListLayer = () => {
  const navigate = useNavigate();

  /* ================= STATE ================= */
  const [allLeads, setAllLeads] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [assignMode, setAssignMode] = useState("assign");

  /* FILTERS */
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");

  /* PAGINATION */
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    loadLeads();
    loadStatuses();
    loadCategories();
  }, []);

  const loadLeads = async () => {
    const res = await getLeads();
    if (res.status === "success") {
      setAllLeads(res.data || []);
    }
  };

  const loadStatuses = async () => {
    const res = await getLeadStatuses();
    if (res.status === "success") {
      setStatuses(res.data || []);
    }
  };

  const loadCategories = async () => {
    const res = await getCategories();
    if (res.status === "success") {
      setCategories(res.data || []);
    }
  };

  /* ================= FORMATTING LOGIC ================= */
  const getDisplayRole = (role) => {
    if (!role) return "-";
    const val = role.toLowerCase().trim();
    if (val === "sales") return "Sales Executive";
    if (val === "tm") return "Team Manager";
    if (val === "admin") return "Admin";
    return role;
  };

  const formatStatusLabel = (status) => {
    if (!status) return "";
    return status
      .toString()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .replace(/\bSram\b/i, "SRAM")
      .replace(/\bDnd\b/i, "DND");
  };

  /* ================= FILTER LOGIC ================= */
const filteredLeads = useMemo(() => {
  return allLeads.filter((l) => {
    let ok = true;

    if (status && l.status !== status) ok = false;

    if (
      roleFilter &&
      l.created_by_role?.toLowerCase() !== roleFilter.toLowerCase()
    ) {
      ok = false;
    }

    // CATEGORY FILTER BY ID + NAME
    if (categoryFilter) {
      const [filterId, filterName] = categoryFilter.split("|");

      const matchesCategory =
        String(l.category_id) === String(filterId) ||
        l.category_name?.toLowerCase() === filterName?.toLowerCase();

      if (!matchesCategory) ok = false;
    }

    if (fromDate && new Date(l.created_at) < new Date(fromDate)) {
      ok = false;
    }

    if (toDate && new Date(l.created_at) > new Date(toDate)) {
      ok = false;
    }

    if (sourceFilter && l.source?.toLowerCase() !== sourceFilter.toLowerCase()) {
      ok = false;
    }

    return ok;
  });
}, [
  allLeads,
  fromDate,
  toDate,
  status,
  roleFilter,
  categoryFilter,
  sourceFilter,
]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filteredLeads.length / limit) || 1;
  const paginatedLeads = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredLeads.slice(start, start + limit);
  }, [filteredLeads, page, limit]);

  useEffect(() => { setPage(1); }, [limit, status, fromDate, toDate, roleFilter, categoryFilter, sourceFilter]);

  /* ================= ACTIONS ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete lead?")) return;
    const res = await deleteLead(id);
    if (res.status === "success") {
      toast.success("Lead deleted");
      loadLeads();
    }
  };

  // ✅ New Bulk Delete Action
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selected.length} leads?`)) return;

    const res = await deleteMultipleLeads(selected);
    if (res.status === "success") {
      toast.success(`${selected.length} Leads deleted successfully`);
      setSelected([]); // Clear selection
      loadLeads();    // Refresh list
    } else {
      toast.error(res.message || "Failed to delete leads");
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    setSelected(
      selected.length === paginatedLeads.length
        ? []
        : paginatedLeads.map((l) => l.id)
    );
  };

  const isAllSelected = selected.length === paginatedLeads.length && paginatedLeads.length > 0;

  return (
    <div className="lead-wrapper">
      <div className="lead-card">
        <div className="lead-header">
          <h5>Lead Management</h5>
          <div className="lead-actions">
            {/* ✅ Bulk Delete Button Added */}
            {selected.length > 0 && (
              <button className="btn-bulk-delete" onClick={handleBulkDelete}>
                <FaTrash /> Delete Selected ({selected.length})
              </button>
            )}

            <button className="btn-upload" onClick={() => setShowUpload(true)}>
              <FaUpload /> Upload CSV
            </button>

            <button
              className="btn-unassign"
              disabled={selected.length === 0}
              onClick={async () => {
                if (selected.length === 0) return;
                if (!window.confirm(`Unassign ${selected.length} selected lead(s)?`)) return;
                const res = await unassignLeads(selected);
                if (res.status === "success") {
                  toast.success(res.message || "Leads unassigned successfully");
                  setSelected([]);
                  loadLeads();
                } else {
                  toast.error(res.message || "Failed to unassign leads");
                }
                if (res.data?.errors?.length) {
                  res.data.errors.forEach((err) => {
                    toast.error(`Lead ${err.lead_id}: ${err.error}`);
                  });
                }
              }}
            >
              <FaUserTimes /> Unassign ({selected.length})
            </button>

            <button
              className="btn-assign"
              disabled={selected.length === 0}
              onClick={() => {
                setAssignMode("reassign");
                setShowAssign(true);
              }}
            >
              <FaUsers /> Reassign ({selected.length})
            </button>

            <button
              className="btn-assign"
              disabled={selected.length === 0}
              onClick={() => {
                setAssignMode("assign");
                setShowAssign(true);
              }}
            >
              <FaUsers /> Assign ({selected.length})
            </button>

            <button className="btn-add" onClick={() => navigate("/add-lead")}>
              <FaPlus /> Add Lead
            </button>
          </div>
        </div>

        {/* ================= FILTER BAR ================= */}
        <div className="filter-bar">
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />

        <select
  value={categoryFilter}
  onChange={(e) => setCategoryFilter(e.target.value)}
>
  <option value="">All Categories</option>

  {categories.map((c) => (
    <option key={c.id} value={`${c.id}|${c.name}`}>
      {c.name}
    </option>
  ))}
</select>

          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <option value="">All Sources</option>
            <option value="Website">Website</option>
            <option value="Facebook">Facebook</option>
            <option value="Google Ads">Google Ads</option>
            <option value="Reference">Reference</option>
          </select>

          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="tm">Team Manager</option>
            <option value="sales">Sales Executive</option>
          </select>

          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All Lead Status</option>
            {[...statuses].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((s) => (
              <option key={s.id} value={s.name}>{formatStatusLabel(s.name)}</option>
            ))}
          </select>

          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
            <option value={200}>200 / page</option>
            <option value={300}>300 / page</option>
            <option value={400}>400 / page</option>
            <option value={500}>500 / page</option>
          </select>

          <button className="btn-reset" onClick={() => {
            setFromDate(""); setToDate(""); setStatus(""); setRoleFilter(""); setCategoryFilter(""); setSourceFilter("");
          }}>Reset</button>

          <div className="pagination" style={{ margin: 0, padding: 0, justifyContent: 'flex-start' }}>
            <button disabled={page === 1} onClick={() => setPage(page - 1)}><FaChevronLeft /></button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage(page + 1)}><FaChevronRight /></button>
          </div>
          
          <a 
            href="/assets/fille/sample_leads_full.csv" 
            download 
            className="btn-download" 
            style={{ 
              padding: "8px 15px", 
              backgroundColor: "#28a745", 
              color: "#fff", 
              borderRadius: "5px", 
              textDecoration: "none", 
              fontWeight: "500",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              marginLeft: "auto"
            }}
          >
            <FaDownload /> Sample File
          </a>
        </div>

        {/* ================= TABLE ================= */}
        <div className="table-responsive">
          <table className="lead-table">
            <thead>
              <tr>
                <th>
                  <div className={`custom-check ${isAllSelected ? "checked" : ""}`} onClick={toggleAll}>
                    {isAllSelected && <FaCheck />}
                  </div>
                </th>
                <th>Name</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Category</th>
                <th>Team Manager</th>
                <th>Sales</th>
                <th>Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLeads.length === 0 ? (
                <tr><td colSpan="9" className="no-data">No leads found</td></tr>
              ) : (
                paginatedLeads.map((l) => {
                  const checked = selected.includes(l.id);
                  const roleSlug = l.created_by_role?.toLowerCase().trim();
                  return (
                    <tr key={l.id} className={checked ? "row-selected" : ""}>
                      <td>
                        <div className={`custom-check ${checked ? "checked" : ""}`} onClick={() => toggleSelect(l.id)}>
                          {checked && <FaCheck />}
                        </div>
                      </td>
                      <td className="font-medium">{l.name}</td>
                      <td>{l.phone}</td>
                      <td>
                        <span className={`role-badge role-${roleSlug}`}>
                          {getDisplayRole(l.created_by_role)}
                        </span>
                      </td>
                      <td>{l.category_name || l.catergory_name || "-"}</td>
                      <td>{l.tm_name || "-"}</td>
                      <td>{l.sales_name || "-"}</td>
                      <td>
                        <span className={`status-pill ${l.status?.toLowerCase()}`}>
                          {l.status}
                        </span>
                      </td>
                      <td className="action-col">
                        <button className="icon-btn view" title="View" onClick={() => navigate(`/view-lead/${l.id}`)}><FaEye /></button>
                        <button className="icon-btn edit" title="Edit" onClick={() => navigate(`/add-lead/${l.id}`)}><FaEdit /></button>
                        <button className="icon-btn delete" title="Delete" onClick={() => handleDelete(l.id)}><FaTrash /></button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}><FaChevronLeft /></button>
          <span>Page {page} of {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage(page + 1)}><FaChevronRight /></button>
        </div>
      </div>

      {showAssign && <AssignLeadModal mode={assignMode} leadIds={selected} onClose={() => setShowAssign(false)} onSuccess={() => { setSelected([]); setShowAssign(false); loadLeads(); }} />}
      {showUpload && <UploadLeadModal onClose={() => setShowUpload(false)} onSuccess={() => { setShowUpload(false); loadLeads(); }} />}
    </div>
  );
};

export default LeadListLayer;